import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BillingPeriod } from '../../../models/functions/billing-period';
import { cn } from '../../../utils/tailwind';
import { prices } from './pricing-data';
import { HomepagePricingPrice } from './homepage-pricing-price';
import { SavingsBadge, PricingReaction } from './pricing-feedback';
import { WebTooltip } from '../../../components/web/tooltip/web-tooltip';
import { IconInfo } from '../../../components/app/icons/info';

interface PremiumPricingCardContentProps {
    available: BillingPeriod[];
    period: BillingPeriod;
    withVat: boolean;
    onPeriodChange: (period: BillingPeriod) => void;
    onVatChange: (withVat: boolean) => void;
    periodSwitchRef?: React.Ref<HTMLDivElement>;
    vatSwitchRef?: React.Ref<HTMLDivElement>;
    reaction?: { period: 'halfyearly' | 'yearly'; id: number } | null;
    action?: React.ReactNode;
    termsInTooltip?: boolean;
}

const PricingVatSwitch: React.FC<{ withVat: boolean; onChange: (value: boolean) => void }> = ({
    withVat,
    onChange,
}) => {
    const { t } = useTranslation();
    return (
        <div
            role="group"
            aria-label={t('pages.homepage.pricingDesign.vatLabel')}
            className="inline-flex shrink-0 rounded-full border border-[#d9cdc5] p-[0.3rem]"
        >
            {[true, false].map((value) => (
                <button
                    key={String(value)}
                    type="button"
                    aria-pressed={withVat === value}
                    onClick={() => onChange(value)}
                    className={cn(
                        'rounded-full px-[1.2rem] py-[0.5rem] text-[1.2rem] font-medium text-web-copy transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary',
                        withVat === value ? 'bg-[#d9cdc5]/40' : 'hover:bg-[#d9cdc5]/20',
                    )}
                >
                    {t(`blocks.pricing.common.${value ? 'withVat' : 'withoutVat'}`)}
                </button>
            ))}
        </div>
    );
};

export const PremiumPricingCardContent: React.FC<PremiumPricingCardContentProps> = ({
    available,
    period,
    withVat,
    onPeriodChange,
    onVatChange,
    periodSwitchRef,
    vatSwitchRef,
    reaction,
    action,
    termsInTooltip = false,
}) => {
    const { t } = useTranslation();
    return (
        <>
            <SavingsBadge period={period} withVat={withVat} />
            {reaction && <PricingReaction key={reaction.id} period={reaction.period} />}
            <div className="mb-[3.2rem] flex flex-wrap items-center justify-between gap-[1.6rem]">
                <div className="flex items-center gap-[0.8rem]">
                    <h3 className="text-[2.4rem] font-bold text-brand-secondary leading-[1.3]">Premium</h3>
                    {termsInTooltip && (
                        <WebTooltip content={t('pages.homepage.pricingDesign.trialTerms')} position="top">
                            <button
                                type="button"
                                aria-label={t('dialogs.signUp.selectedPlan.termsInfo')}
                                className="flex h-[2.4rem] w-[2.4rem] items-center justify-center rounded-full text-web-copy hover:bg-[#d9cdc5]/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-web-copy"
                            >
                                <IconInfo />
                            </button>
                        </WebTooltip>
                    )}
                </div>
                {available.length > 1 ? (
                    <div
                        ref={periodSwitchRef}
                        role="group"
                        aria-label={t('pages.homepage.pricingConcepts.periodLabel')}
                        className="inline-grid max-w-full grid-cols-3 gap-[0.2rem] rounded-full border border-[#d9cdc5] p-[0.3rem]"
                    >
                        {available.map((option) => (
                            <button
                                key={option}
                                type="button"
                                aria-pressed={period === option}
                                onClick={() => onPeriodChange(option)}
                                className={cn(
                                    'min-w-0 rounded-full px-[1.4rem] py-[0.7rem] text-[1.2rem] font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary',
                                    period === option
                                        ? 'bg-[#d9cdc5]/40 text-web-copy'
                                        : 'text-web-copy hover:bg-[#d9cdc5]/40',
                                )}
                            >
                                {t(`pages.homepage.pricingConcepts.periods.${option}`)}
                            </button>
                        ))}
                    </div>
                ) : (
                    <p className="text-[1.5rem] text-web-copy">{t(`blocks.pricing.common.${prices[period].label}`)}</p>
                )}
            </div>
            <div aria-live="polite" aria-atomic="true">
                <HomepagePricingPrice period={period} withVat={withVat} />
            </div>
            <div className="my-[2.4rem] flex items-center gap-[1.6rem]">
                <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-[#d9cdc5]" />
                <div ref={vatSwitchRef} className="inline-flex shrink-0">
                    <PricingVatSwitch withVat={withVat} onChange={onVatChange} />
                </div>
                <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-[#d9cdc5]" />
            </div>
            <div>
                <p
                    className={cn('text-[1.5rem] leading-relaxed text-web-copy', Boolean(action) && 'mb-[2.4rem]')}
                    aria-live="polite"
                    aria-atomic="true"
                >
                    <Trans
                        i18nKey={`pages.homepage.pricingDesign.trialFeatures.${period}`}
                        components={{ strong: <strong className="font-bold text-brand-secondary" /> }}
                    />
                </p>
                {action}
                {!termsInTooltip && (
                    <p className="mt-[1.6rem] text-[1.3rem] leading-relaxed text-web-copy">
                        {t('pages.homepage.pricingDesign.trialTerms')}
                    </p>
                )}
            </div>
        </>
    );
};
